// WhatsappIntegrationView — Settings panel for the browser-extension that
// logs WhatsApp Web conversations into Deskte. Lets the agent (or a seat
// with clients.edit) generate / view / revoke a long-lived extension token.
//
// The raw token is shown EXACTLY ONCE on generation; thereafter only the
// last 4 chars + last-used time are available (the backend stores a hash).
import React, { useState, useEffect, useCallback } from "react";
import { Loader2, MessageSquare, Copy, Check, RefreshCw, Trash2, ShieldCheck } from "lucide-react";
import {
  getWhatsappExtensionToken,
  generateWhatsappExtensionToken,
  revokeWhatsappExtensionToken,
} from "../api.js";
import { confirmDialog } from "./ui.jsx";
import { copyText } from "../utils.js";

export function WhatsappIntegrationView({ lang = "en" }) {
  const t = (es, en) => (lang === "es" ? es : en);

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [freshToken, setFreshToken] = useState(""); // shown once
  const [copied, setCopied] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setStatus(await getWhatsappExtensionToken());
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  async function handleGenerate() {
    if (status?.hasToken) {
      const ok = await confirmDialog({
        message: t(
          "Ya tienes un token activo. Generar uno nuevo hará que el anterior deje de funcionar (tendrás que pegarlo de nuevo en la extensión). ¿Continuar?",
          "You already have an active token. Generating a new one will stop the old one from working (you'll need to paste the new one into the extension). Continue?",
        ),
      });
      if (!ok) return;
    }
    setBusy(true);
    setError("");
    setFreshToken("");
    try {
      const data = await generateWhatsappExtensionToken();
      setFreshToken(data.token);
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleRevoke() {
    const ok = await confirmDialog({
      message: t(
        "¿Revocar el token? La extensión dejará de poder guardar conversaciones hasta que generes y pegues uno nuevo.",
        "Revoke the token? The extension will stop saving conversations until you generate and paste a new one.",
      ),
      danger: true,
    });
    if (!ok) return;
    setBusy(true);
    setError("");
    setFreshToken("");
    try {
      await revokeWhatsappExtensionToken();
      await refresh();
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setBusy(false);
    }
  }

  function copyToken() {
    copyText(freshToken).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const card = {
    background: "var(--c-card,#0f2433)", border: "1px solid var(--c-border,#1c3a4f)",
    borderRadius: 12, padding: "1.25rem", marginBottom: "1rem",
  };
  const btn = (bg, fg = "#fff") => ({
    display: "inline-flex", alignItems: "center", gap: 8, padding: "9px 14px",
    background: bg, color: fg, border: "none", borderRadius: 8, fontSize: 14,
    fontWeight: 600, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1,
  });

  return (
    <div style={{ padding: "1.5rem", maxWidth: 760, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
        <MessageSquare size={22} style={{ color: "#22c55e" }} />
        <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>
          {t("Integración WhatsApp", "WhatsApp Integration")}
        </h1>
      </div>
      <p style={{ color: "var(--c-text2,#7aa3c0)", fontSize: 13, marginTop: 0, marginBottom: 18, lineHeight: 1.5 }}>
        {t(
          "Guarda conversaciones de WhatsApp Web dentro de la ficha del cliente, de forma automática, usando la extensión de navegador de Deskte. Mientras tengas WhatsApp Web abierto, los chats que abras se guardan solos (empareja por teléfono o por nombre del contacto). Sin Twilio, sin costo.",
          "Save WhatsApp Web conversations into the client's profile automatically, using the Deskte browser extension. While WhatsApp Web is open, the chats you open are saved on their own (matched by phone or contact name). No Twilio, no cost.",
        )}
      </p>

      {error && (
        <div style={{ ...card, background: "#3a1620", borderColor: "#7f1d1d", color: "#fecaca" }}>{error}</div>
      )}

      {loading ? (
        <div style={{ ...card, display: "flex", alignItems: "center", gap: 10, color: "var(--c-text2,#7aa3c0)" }}>
          <Loader2 size={16} className="dkt-spin" /> {t("Cargando…", "Loading…")}
        </div>
      ) : (
        <>
          {/* Freshly generated token — shown once */}
          {freshToken && (
            <div style={{ ...card, background: "#06281c", borderColor: "#14532d" }}>
              <div style={{ fontWeight: 700, color: "#bbf7d0", marginBottom: 8 }}>
                {t("✅ Token generado — cópialo ahora", "✅ Token generated — copy it now")}
              </div>
              <div style={{ fontSize: 12, color: "#86efac", marginBottom: 10 }}>
                {t(
                  "Por seguridad solo se muestra una vez. Pégalo en la extensión (ícono de Deskte en el navegador).",
                  "For security it is shown only once. Paste it into the extension (Deskte icon in your browser).",
                )}
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <code style={{
                  flex: 1, background: "#03150f", padding: "10px 12px", borderRadius: 8,
                  fontSize: 13, wordBreak: "break-all", color: "#d1fae5", border: "1px solid #14532d",
                }}>{freshToken}</code>
                <button onClick={copyToken} style={btn("#16a34a")}>
                  {copied ? <Check size={16} /> : <Copy size={16} />}
                  {copied ? t("Copiado", "Copied") : t("Copiar", "Copy")}
                </button>
              </div>
            </div>
          )}

          {/* Current status */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
              <div>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>
                  {status?.hasToken
                    ? t("Token activo", "Active token")
                    : t("Sin token", "No token yet")}
                </div>
                <div style={{ fontSize: 13, color: "var(--c-text2,#7aa3c0)" }}>
                  {status?.hasToken ? (
                    <>
                      <span>•••• {status.lastFour}</span>
                      {status.lastUsedAt
                        ? <span> · {t("último uso", "last used")}: {new Date(status.lastUsedAt).toLocaleString("en-US")}</span>
                        : <span> · {t("aún no usado", "not used yet")}</span>}
                      {/* Caduca a los 90 días (auditoría 2026-09-27): se dice aquí,
                          no se descubre cuando la extensión deja de guardar. */}
                      {status.expiresAt && (
                        <span style={{ color: status.expired ? "var(--c-red,#ff3d6b)" : undefined }}>
                          {" · "}{status.expired
                            ? t("CADUCADO: genera uno nuevo", "EXPIRED: generate a new one")
                            : `${t("caduca", "expires")}: ${new Date(status.expiresAt).toLocaleDateString("en-US")}`}
                        </span>
                      )}
                    </>
                  ) : (
                    t("Genera un token para conectar la extensión.", "Generate a token to connect the extension.")
                  )}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={handleGenerate} disabled={busy} style={btn("#2563eb")}>
                  {status?.hasToken ? <RefreshCw size={16} /> : <MessageSquare size={16} />}
                  {status?.hasToken ? t("Regenerar", "Regenerate") : t("Generar token", "Generate token")}
                </button>
                {status?.hasToken && (
                  <button onClick={handleRevoke} disabled={busy} style={btn("transparent", "#fca5a5")}>
                    <Trash2 size={16} /> {t("Revocar", "Revoke")}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Install steps */}
          <div style={card}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>
              {t("Cómo instalar la extensión", "How to install the extension")}
            </div>
            <a href="https://chromewebstore.google.com/detail/iajbhhkegljfohmlljicioopagpoejme" target="_blank" rel="noreferrer"
              style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#2563eb", color: "#fff", fontWeight: 700, fontSize: 13.5, textDecoration: "none", padding: "10px 16px", borderRadius: 9, marginBottom: 12 }}>
              🧩 {t("Instalar extensión (Chrome Web Store)", "Install extension (Chrome Web Store)")}
            </a>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.7, color: "var(--c-text2,#7aa3c0)" }}>
              <li>{t("Haz clic en el botón de arriba y luego en “Agregar a Chrome” (funciona también en Microsoft Edge).", "Click the button above, then “Add to Chrome” (works in Microsoft Edge too).")}</li>
              <li>{t("Genera tu token arriba y cópialo.", "Generate your token above and copy it.")}</li>
              <li>{t("Haz clic en el ícono de Deskte en tu navegador y pega el token (deja activado “Guardado automático”).", "Click the Deskte icon in your browser and paste the token (leave “Auto-save” turned on).")}</li>
              <li>{t("Abre web.whatsapp.com y abre el chat del cliente: se guarda solo. (También está el botón “📥 Guardar en Deskte” para hacerlo manual.)", "Open web.whatsapp.com and open the client's chat: it saves on its own. (There's also a “📥 Save to Deskte” button for manual saving.)")}</li>
            </ol>
          </div>

          {/* HIPAA note */}
          <div style={{ ...card, display: "flex", gap: 10, alignItems: "flex-start", background: "#1a2433", borderColor: "#334155" }}>
            <ShieldCheck size={18} style={{ color: "#fbbf24", flexShrink: 0, marginTop: 2 }} />
            <div style={{ fontSize: 12.5, color: "#cbd5e1", lineHeight: 1.5 }}>
              {t(
                "Las conversaciones se guardan cifradas dentro de Deskte. WhatsApp/Meta no firma acuerdo HIPAA: úsalo solo para coordinar (citas, recordatorios, documentos), nunca para diagnósticos ni datos médicos sensibles.",
                "Conversations are stored encrypted inside Deskte. WhatsApp/Meta does not sign a HIPAA agreement: use it only for coordination (appointments, reminders, documents), never for diagnoses or sensitive medical data.",
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
